import {useRef} from "react";
import {useIntersectionObserver} from "./useIntersectionObserver";

/**
 * Manages scroll-spy behavior: tracks which checklist item is visible
 * in the scroll container, highlights the corresponding TOC entry,
 * and provides helpers for programmatic scrolling.
 *
 * A `locked` mechanism prevents the observer from overriding the active
 * TOC item during programmatic scroll-to-item (e.g. TOC click).
 */
export const useChecklistScrollSpy = (
    tocParentMap: Record<string, string>,
) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const tocContainerRef = useRef<HTMLDivElement>(null);
    const locked = useRef(false);
    const manuallySetActiveElementId = useRef<string | null>(null);
    const activeElementRef = useRef<string | null>(null);

    const scrollToCheckItem = (id: string) => {
        document
            .querySelector(`[data-id="${id}"]`)
            ?.scrollIntoView({behavior: "auto", block: "start"});
    };

    const scrollToActiveTocItem = (id: string) => {
        document
            .querySelector(`[data-id="toc-item-${id}"]`)
            ?.scrollIntoView({behavior: "auto", block: "center"});
    };

    const setActiveTocItem = (newId: string | null) => {
        const oldId = activeElementRef.current;
        if (newId === oldId) return;
        if (oldId)
            document
                .querySelector(`[data-id="toc-item-${oldId}"]`)
                ?.removeAttribute("data-toc-active");
        if (newId)
            document
                .querySelector(`[data-id="toc-item-${newId}"]`)
                ?.setAttribute("data-toc-active", "");
        activeElementRef.current = newId;
    };

    const {observeElement, unobserveElement} = useIntersectionObserver(
        {
            root: containerRef as React.RefObject<Element | null | undefined>,
            rootMargin: "0px 0px 0px 0px",
        },
        {
            onActiveElementChange: ({
                activeElementId,
                intersectingElements,
            }) => {
                if (locked.current) {
                    if (
                        manuallySetActiveElementId.current &&
                        intersectingElements.has(
                            manuallySetActiveElementId.current,
                        )
                    ) {
                        locked.current = false;
                        manuallySetActiveElementId.current = null;
                    }
                    return;
                }
                setActiveTocItem(activeElementId ?? null);
                setTimeout(
                    () => scrollToActiveTocItem(activeElementId ?? ""),
                    0,
                );
            },
            nodeParentMap: tocParentMap,
        },
    );

    return {
        containerRef,
        tocContainerRef,
        locked,
        manuallySetActiveElementId,
        observeElement,
        unobserveElement,
        scrollToCheckItem,
        scrollToActiveTocItem,
        setActiveTocItem,
    };
};
